import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import "./styles/global.scss";
import "./styles/theme.scss";
import PageWrapper from "./layouts/PageWrapper";
import { getFilterConfig } from "./lib/api";
import { postMessageReviver, sendMessage } from "./lib/postMessage";
import { MARKETS_VIEW, ODDS_VIEW } from "./constants";
import { HEIGHT_CHANGED_MESSAGE_TYPE } from "./constants/messages";
import {
  ADD_TO_ODDS_SELECTIONS,
  ADD_TO_FILTER_SELECTIONS,
  SET_ACTIVE_FILTER_VIEW,
  SET_PP_CONFIG_DATA,
} from "./constants/actions";

function App() {
  const appWrapperRef = useRef(null);
  const dispatch = useDispatch();

  const navigateToFilterView = useCallback(
    (filterView) => {
      if (!filterView) return;

      const { view, filterSelections } = filterView;

      if (view === ODDS_VIEW) {
        dispatch({
          type: ADD_TO_ODDS_SELECTIONS,
          payload: {
            filter: view,
            odds: filterSelections,
          },
        });
      }

      if (view === MARKETS_VIEW) {
        dispatch({
          type: ADD_TO_FILTER_SELECTIONS,
          payload: {
            filter: view,
            type: filterSelections,
          },
        });
      }

      dispatch({ type: SET_ACTIVE_FILTER_VIEW, payload: view });
      localStorage.removeItem("login");
    },
    [dispatch]
  );

  const getConfigData = useCallback(async () => {
    try {
      const res = await getFilterConfig();
      if (!res) return;

      dispatch({
        type: SET_PP_CONFIG_DATA,
        payload: res,
      });

      const storedFilterView = localStorage.getItem("login");
      if (storedFilterView) {
        try {
          const filterView = JSON.parse(storedFilterView);
          navigateToFilterView(filterView);
        } catch (error) {
          console.error("Error parsing stored filter view:", error);
          localStorage.removeItem("login");
        }
      }
    } catch (error) {
      console.error("Error fetching config data:", error);
    }
  }, [dispatch, navigateToFilterView]);

  useEffect(() => {
    getConfigData();
  }, [getConfigData]);

  const receiveMessage = useCallback(
    (event) => {
      if (!event?.data || typeof event.data !== "string") return;

      // Skip webpack hot reload messages
      if (event.data.startsWith("webpack")) return;

      try {
        const received = JSON.parse(event.data, postMessageReviver);
        dispatch({
          type: received.type,
          payload: received.payload,
        });
      } catch (error) {
        console.error("Error processing received message:", error);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    window.addEventListener("message", receiveMessage, false);
    return () => window.removeEventListener("message", receiveMessage, false);
  }, [receiveMessage]);

  const sendHeightUpdate = (height) => {
    sendMessage({
      type: HEIGHT_CHANGED_MESSAGE_TYPE,
      payload: { height },
    });
  };

  useEffect(() => {
    let timeoutId = null;

    // Simple debounce function that cancels previous timeout
    const debouncedHeightUpdate = (height) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        sendHeightUpdate(height);
      }, 100);
    };

    // Create an observer instance to watch for height changes
    const observer = new ResizeObserver((entries) => {
      const { height } = entries[0].contentRect;
      // Skip updates if height is 0 to prevent unnecessary messages
      if (height === 0) return;

      // Send debounced height update to parent window
      debouncedHeightUpdate(height);
    });

    // Start observing the app wrapper element
    if (appWrapperRef.current) {
      observer.observe(appWrapperRef.current);
    }

    // Cleanup: disconnect observer and clear any pending timeout
    return () => {
      observer.disconnect();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <div ref={appWrapperRef}>
      <PageWrapper />
    </div>
  );
}

export default App;
